import React, { createContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [emailId, setEmailId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNo, setPhoneNo] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [newRegistered, setNewRegistered] = useState(false);
  const [party, setParty] = useState(null);
  const [isCandidate, setIsCandidate] = useState(false);
  const [candidateState, setCandidateState] = useState('');
  const [allVoted, setAllVoted] = useState(false);
  const [electoralDivision, setElectoralDivision] = useState('');
  const [address, setAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [quizFinished, setQuizFinished] = useState(false);

  const initialRedirectDone = useRef(false);

  useEffect(() => {
    const storedEmailId = localStorage.getItem('emailId');
    const storedToken = localStorage.getItem('token');
    const storedFirstName = localStorage.getItem('firstName');

    if (storedEmailId && storedToken && !initialRedirectDone.current) {
      setEmailId(storedEmailId);
      setFirstName(storedFirstName);
      setIsLoggedIn(true);
      
      // Only redirect to /main if user is on login page or root path
      const currentPath = window.location.pathname;
      if (currentPath === '/' ) {
        window.location.href = '/main';
      }
      
      initialRedirectDone.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (emailId) {
        try {
          const response = await fetch('/api/get-user-data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emailId })
          });
          if (response.ok) {
            const data = await response.json();
            setFirstName(data.firstName);
            setLastName(data.lastName);
            setPhoneNo(data.phoneNo);
            setState(data.state);
            setPostcode(data.postcode);
            setParty(data.party);
            setIsCandidate(data.is_candidate === 1);
            setCandidateState(data.candidate_state);
            setAllVoted(data.all_voted === 1);
            setElectoralDivision(data.electoral_division);
            setAddress(data.address);
            setSuburb(data.suburb);
            setQuizFinished(data.quiz_finished === 1);
            setIsLoggedIn(true);
            setNewRegistered(false);
          } else {
            console.error('Failed to fetch user data');
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsLoggedIn(false);
        }
      } else {
        setIsLoggedIn(false);
      }
    };

    fetchUserData();
  }, [emailId]);

  const fetchUserData = async (token) => {
    try {
      setLoading(true);
      const response = await axios.get("/api/get-user-data", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const data = response.data;
        setUser(data);
        setEmailId(data.email_id);
        setPhoneNo(data.phone_no);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        setState(data.state);
        setIsCandidate(data.is_candidate === 1);
        setCandidateState(data.candidate_state);
        setAllVoted(data.all_voted === 1);
        setElectoralDivision(data.electoral_division);
        setAddress(data.address);
        setSuburb(data.suburb);
        setQuizFinished(data.quiz_finished === 1);
        setIsLoggedIn(true);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Failed to fetch user data");
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        setIsLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserContext.Provider value={{ 
      user,
      loading,
      error,
      isLoggedIn,
      emailId,
      phoneNo,
      firstName,
      lastName,
      state,
      postcode,
      newRegistered,
      party,
      isCandidate,
      candidateState,
      allVoted,
      electoralDivision,
      address,
      electoralDivision,
      suburb,
      quizFinished,
      setElectoralDivision,
      setAllVoted,
      setIsLoggedIn,
      setEmailId,
      setFirstName,
      setLastName,
      setPhoneNo,
      setState,
      setPostcode,
      setNewRegistered,
      setParty,
      setIsCandidate,
      setCandidateState,
      setElectoralDivision,
      setAddress,
      setSuburb,
      setQuizFinished,
      fetchUserData
    }}>
      {children}
    </UserContext.Provider>
  );
};